// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE REST OF VERIFICATION'S "ACTION" SURFACES, ON THE GUIDED DOCUMENTS PAGE (approved retirement plan items
// 5, 7 and 9, 2026-10-16). 🚨 NO NEW CONTRACTS — each calls exactly what the Verification page calls:
//   · RequestsWeSent  — gift letter (/api/gift-letter/my-requests + /resend/:id), VOR (/api/documents/vor-requests/my
//                       + /vor-requests/resend/:id), net sheet (POST /request-net-sheet) and rent-free
//                       (POST /request-rent-free); the last two have no resend route, so Resend re-sends the same
//                       request with the same details, as Verification's form does.
//   🚨 NO UPLOADS HERE (Jesse, 2026-10-28): the flagged-section upload and the post-approval "Add assets" re-price were
//   the last two. A section flag is a step-5 request now (backend utils/sectionFlagNeed), answered on its card, opened in
//   view mode, never moving the borrower. The post-decision "Add assets" is removed (Jesse, 2026-11-02).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { shownToBorrower } from '../constants/borrowerRequestVisibility';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const btn = { fontSize: 12.5, fontWeight: 700, color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', padding: 0 };
const row = { borderTop: '1px solid #fde68a', padding: '10px 0', fontSize: 13, color: '#78350f' };
const STATUS_WORDS = { pending: 'Waiting for them', completed: 'Received', expired: 'Expired', sent: 'Sent' };

// ── Requests we sent for you ───────────────────────────────────────────────
export function RequestsWeSent({ token, netSheet, rentFree, onChanged }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [gift, setGift] = useState([]);
  const [vor, setVor] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/api/gift-letter/my-requests`, { headers: h }).then((r) => setGift(r.data?.requests || [])).catch(() => setGift([]));
    axios.get(`${API}/api/documents/vor-requests/my`, { headers: h }).then((r) => setVor(r.data?.requests || [])).catch(() => setVor([]));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const resend = async (key, call) => {
    setBusy(key); setMsg(null);
    try { await call(); setMsg({ ok: true, text: 'Sent again.' }); load(); onChanged && onChanged(); }
    catch (e) { setMsg({ ok: false, text: e?.response?.data?.error || 'That did not go through. Please try again.' }); }
    finally { setBusy(null); }
  };

  // 🔑 A cancelled request is never listed (Hannah's review, 2026-11-02 item 5 — constants/borrowerRequestVisibility).
  const items = [
    ...gift.filter(shownToBorrower).map((g) => ({ key: `gift-${g.id}`, kind: 'Gift letter', who: g.donor_name, email: g.donor_email, status: g.status, at: g.created_at,
      resend: g.status === 'pending' ? () => axios.post(`${API}/api/gift-letter/resend/${g.id}`, {}, { headers }) : null })),
    ...vor.filter(shownToBorrower).map((v) => ({ key: `vor-${v.id}`, kind: 'Verification of rent', who: v.landlord_name, email: v.landlord_email, status: v.status, at: v.created_at,
      resend: v.status === 'pending' ? () => axios.post(`${API}/api/documents/vor-requests/resend/${v.id}`, {}, { headers }) : null })),
    ...(netSheet || []).filter(shownToBorrower).map((n) => ({ key: `ns-${n.id}`, kind: 'Net sheet', who: n.escrow_agent_name, email: n.escrow_agent_email, status: 'sent', at: n.sent_at,
      resend: () => axios.post(`${API}/api/documents/request-net-sheet`,
        { escrow_agent_name: n.escrow_agent_name, escrow_agent_email: n.escrow_agent_email, property_address: n.property_address }, { headers }) })),
    ...(rentFree || []).filter(shownToBorrower).map((r) => ({ key: `rf-${r.id}`, kind: 'Rent-free letter', who: r.owner_name, email: r.owner_email, status: r.status, at: r.created_at,
      resend: r.status === 'pending' ? () => axios.post(`${API}/api/documents/request-rent-free`,
        { owner_name: r.owner_name, owner_email: r.owner_email, property_address: r.property_address || '' }, { headers }) : null })),
  ];
  if (items.length === 0) return null;

  return (
    <div data-testid="documents-requests" style={{ marginTop: 6 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: '#92400e', borderTop: '1px solid #fde68a', paddingTop: 10 }}>Requests we sent for you</div>
      {items.map((it) => (
        <div key={it.key} data-testid="documents-request" style={{ ...row, borderTop: 'none', paddingTop: 6 }}>
          <div><span style={{ fontWeight: 700 }}>{it.kind}</span> — {it.who}{it.email ? ` (${it.email})` : ''}</div>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            <span data-testid="documents-request-status">{STATUS_WORDS[it.status] || it.status}</span>{it.at ? ` · sent ${fmtDate(it.at)}` : ''}
          </div>
          {it.resend ? (
            <button type="button" data-testid="documents-request-resend" style={{ ...btn, marginTop: 4 }} disabled={busy === it.key}
              onClick={() => resend(it.key, it.resend)}>{busy === it.key ? 'Sending…' : 'Resend'}</button>
          ) : null}
        </div>
      ))}
      {msg ? <div role="status" data-testid="documents-request-msg" style={{ fontSize: 12, color: msg.ok ? '#166534' : '#991b1b' }}>{msg.text}</div> : null}
    </div>
  );
}
